// Cleans an analytics event before it leaves the browser. A page view carries the full URL,
// and some Uptrack URLs hold secrets or personal data: the magic-link page has the sign-in
// token and the email address in its query string, an invitation URL has the invitation
// token in its path, and `?url=` holds a customer's site. Only the path is useful for
// analytics, so the query string and the hash are removed, and the invitation token is
// replaced.

const INVITATION_PATH = /^(\/invitations\/)[^/?#]+/

function scrubPath(path: string): string {
  return path.replace(INVITATION_PATH, '$1:token')
}

/** `https://uptrack.app/invitations/abc?x=1#y` becomes `https://uptrack.app/invitations/:token`. */
export function scrubUrl(value: string): string {
  try {
    const u = new URL(value)
    return `${u.origin}${scrubPath(u.pathname)}`
  } catch {
    // Not an absolute URL (a bare path): cut at the first ? or #.
    return scrubPath(value.split(/[?#]/)[0])
  }
}

const URL_KEY = /(^|_)(url|referrer)$/i
const PATH_KEY = /pathname$/i

function scrubValue(key: string, value: unknown): unknown {
  if (typeof value === 'string') {
    if (URL_KEY.test(key)) return scrubUrl(value)
    if (PATH_KEY.test(key)) return scrubPath(value)
    return value
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return scrubProperties(value as Record<string, unknown>)
  }
  return value
}

function scrubProperties(props: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(props).map(([k, v]) => [k, scrubValue(k, v)]))
}

/** For the PostHog `before_send` option: the same event, with clean URLs. */
export function scrubAnalyticsEvent<T extends { properties?: Record<string, unknown> }>(event: T | null): T | null {
  if (!event?.properties) return event
  // `$set` and `$set_once` hold the first URL and referrer too: scrubProperties goes into them.
  return { ...event, properties: scrubProperties(event.properties) }
}

// ── Sentry ──────────────────────────────────────────────────────────────────
// The browser SDK adds the page URL, with its query string, to every event, and URLs to the
// navigation and fetch breadcrumbs. Same rule as above: path only. The functions take any
// object so they fit the SDK's own event and breadcrumb types.

type Loose = Record<string, unknown>

export function scrubSentryBreadcrumb<T extends object>(crumb: T | null): T | null {
  const c = crumb as Loose | null
  if (!c || !c.data || typeof c.data !== 'object') return crumb
  const data = { ...(c.data as Loose) }
  for (const key of ['from', 'to', 'url']) {
    if (typeof data[key] === 'string') data[key] = scrubUrl(data[key] as string)
  }
  return { ...c, data } as T
}

export function scrubSentryEvent<T extends object>(event: T): T {
  const next: Loose = { ...(event as Loose) }
  const req = next.request as Loose | undefined
  if (req && typeof req === 'object') {
    const request: Loose = { ...req }
    delete request.query_string
    if (typeof request.url === 'string') request.url = scrubUrl(request.url)
    const headers = request.headers as Record<string, string> | undefined
    if (headers && typeof headers.Referer === 'string') {
      request.headers = { ...headers, Referer: scrubUrl(headers.Referer) }
    }
    next.request = request
  }
  if (Array.isArray(next.breadcrumbs)) {
    next.breadcrumbs = (next.breadcrumbs as object[]).map((c) => scrubSentryBreadcrumb(c))
  }
  return next as T
}
